import { gql, useQuery } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Search, 
  Folder, 
  FolderOpen, 
  FileCode, 
  ChevronRight, 
  ChevronDown, 
  ArrowLeft, 
  Box, 
  Braces, 
  Activity,
  Server,
  Database,
  Code
} from 'lucide-react';
import { cn } from '@/lib/utils';

const GET_GQL_SCHEMA = gql`
  query GetGqlSchema {
    getGqlSchema
  }
`;

type SchemaKind = 'query' | 'mutation' | 'subscription';

type Endpoint = {
  name: string;
  kind: SchemaKind;
  returnType?: string;
  parameters?: Record<string, unknown>;
  description?: string;
  deprecated?: boolean;
  path?: string;
  folderName?: string;
};

type SchemaPayload = {
  endpoints?: Endpoint[];
};

type FunctionItem = Endpoint & {
  id: string;
};

type FolderNode = {
  id: string;
  label: string;
  depth: number;
  items: FunctionItem[];
  children: FolderNode[];
};

type FolderTree = {
  nodes: FolderNode[];
  nodeMap: Map<string, FolderNode>;
};

const KIND_LABELS: Record<SchemaKind, string> = {
  query: 'Query',
  mutation: 'Mutation',
  subscription: 'Subscription'
};

const KIND_ICONS: Record<SchemaKind, React.ReactNode> = {
  query: <Database className="h-4 w-4 text-emerald-600" />,
  mutation: <Server className="h-4 w-4 text-amber-600" />,
  subscription: <Activity className="h-4 w-4 text-purple-600" />
};

const parseSchemaPayload = (raw?: string | null): SchemaPayload => {
  if (!raw) {
    return { endpoints: [] };
  }
  try {
    const parsed = JSON.parse(raw);
    const endpoints = Array.isArray(parsed?.endpoints) ? parsed.endpoints : [];
    return { endpoints };
  } catch (error) {
    return { endpoints: [] };
  }
};

const normalizeTypeName = (value: string) => `${value || ''}`.replace(/[!\[\]]/g, '').trim();

const toTypeName = (value: unknown) => {
  if (!value) {
    return '';
  }
  if (typeof value === 'string') {
    return normalizeTypeName(value);
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (typeof record.type === 'string') {
      return normalizeTypeName(record.type);
    }
  }
  return '';
};

const formatParameters = (parameters?: Record<string, unknown>) => {
  if (!parameters) {
    return [];
  }
  return Object.entries(parameters).map(([key, value]) => ({
    name: key,
    type: typeof value === 'string' ? value : JSON.stringify(value)
  }));
};

const getPathSegments = (item: FunctionItem) => {
  const raw = `${item.path || ''}`.trim();
  if (!raw) {
    const folder = `${item.folderName || ''}`.trim();
    if (!folder) {
      return ['Uncategorized'];
    }
    return [folder];
  }
  const segments = raw.split('/').filter(Boolean);
  if (segments.length === 0) {
    return ['Uncategorized'];
  }
  const last = segments[segments.length - 1];
  if (last === 'schema') {
    segments.pop();
  }
  return segments.length > 0 ? segments : ['Uncategorized'];
};

const buildFolderTree = (items: FunctionItem[]): FolderTree => {
  const itemMap = new Map(items.map((item) => [item.id, item]));

  type RawNode = {
    id: string;
    label: string;
    depth: number;
    itemIds: Set<string>;
    children: Map<string, RawNode>;
  };

  const root: RawNode = {
    id: 'root',
    label: 'Root',
    depth: -1,
    itemIds: new Set(),
    children: new Map()
  };

  items.forEach((item) => {
    const segments = getPathSegments(item);
    let current = root;
    segments.forEach((segment, index) => {
      const nodeId = index === 0 ? segment : `${current.id}/${segment}`;
      let child = current.children.get(nodeId);
      if (!child) {
        child = {
          id: nodeId,
          label: segment,
          depth: current.depth + 1,
          itemIds: new Set(),
          children: new Map()
        };
        current.children.set(nodeId, child);
      }
      child.itemIds.add(item.id);
      current = child;
    });
  });

  const nodeMap = new Map<string, FolderNode>();

  const finalizeNode = (node: RawNode): FolderNode => {
    const children = Array.from(node.children.values())
      .map((child) => finalizeNode(child))
      .sort((a, b) => a.label.localeCompare(b.label));
    const itemsList = Array.from(node.itemIds)
      .map((id) => itemMap.get(id))
      .filter((entry): entry is FunctionItem => Boolean(entry))
      .sort((a, b) => a.name.localeCompare(b.name));
    const finalNode: FolderNode = {
      id: node.id,
      label: node.label,
      depth: node.depth,
      items: itemsList,
      children
    };
    nodeMap.set(finalNode.id, finalNode);
    return finalNode;
  };

  const nodes = Array.from(root.children.values())
    .map((child) => finalizeNode(child))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { nodes, nodeMap };
};

const buildFolderId = (item: FunctionItem) => getPathSegments(item).join('/');

const toDomId = (prefix: string, value: string) => {
  const normalized = `${value || ''}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${prefix}-${normalized || 'item'}`;
};

export const GqlDevGuidePage = () => {
  const { data, loading, error } = useQuery<{ getGqlSchema: string }>(GET_GQL_SCHEMA);
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedKind, setSelectedKind] = useState<SchemaKind | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<string[]>([]);

  const payload = useMemo(() => parseSchemaPayload(data?.getGqlSchema), [data?.getGqlSchema]);
  const functionItems = useMemo(() => {
    const endpoints = payload.endpoints || [];
    return endpoints.map((entry) => ({
      ...entry,
      id: `${entry.kind}:${entry.name}`
    }));
  }, [payload.endpoints]);

  const itemsById = useMemo(() => new Map(functionItems.map((item) => [item.id, item])), [functionItems]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextSearch = `${params.get('search') || ''}`.trim();
    const nextKind = `${params.get('kind') || ''}`.trim().toLowerCase();
    const focus = `${params.get('focus') || ''}`.trim();

    if (nextSearch !== searchTerm) {
      setSearchTerm(nextSearch);
    }

    if (nextKind === 'query' || nextKind === 'mutation' || nextKind === 'subscription') {
      if (nextKind !== selectedKind) {
        setSelectedKind(nextKind);
      }
    } else if (selectedKind) {
      setSelectedKind(null);
    }

    if (focus) {
      const match = functionItems.find((item) => item.name === focus);
      if (match && match.id !== selectedItemId) {
        setSelectedItemId(match.id);
        const folderId = buildFolderId(match);
        setSelectedFolderId(folderId || null);
        if (folderId) {
          const parts = folderId.split('/');
          const pathIds = parts.map((_, index) => parts.slice(0, index + 1).join('/'));
          setExpandedFolders((prev) => Array.from(new Set([...prev, ...pathIds])));
        }
      }
    }
  }, [location.search, searchTerm, selectedKind, selectedItemId, functionItems]);

  const filteredBySearch = useMemo(() => {
    const trimmed = searchTerm.trim().toLowerCase();
    if (!trimmed) {
      return functionItems;
    }
    return functionItems.filter((item) => item.name.toLowerCase().includes(trimmed));
  }, [functionItems, searchTerm]);

  const filteredByKind = useMemo(() => {
    if (!selectedKind) {
      return filteredBySearch;
    }
    return filteredBySearch.filter((item) => item.kind === selectedKind);
  }, [filteredBySearch, selectedKind]);

  const folderTree = useMemo(() => buildFolderTree(filteredByKind), [filteredByKind]);

  useEffect(() => {
    if (selectedFolderId && !folderTree.nodeMap.has(selectedFolderId)) {
      setSelectedFolderId(null);
    }
  }, [selectedFolderId, folderTree.nodeMap]);

  const selectedFolderItems = useMemo(() => {
    if (!selectedFolderId) {
      return filteredByKind;
    }
    const node = folderTree.nodeMap.get(selectedFolderId);
    if (!node) {
      return filteredByKind;
    }
    return node.items;
  }, [filteredByKind, selectedFolderId, folderTree.nodeMap]);

  const selectedItem = selectedItemId ? itemsById.get(selectedItemId) : undefined;

  const kindCounts = useMemo(() => {
    const counts = { query: 0, mutation: 0, subscription: 0 } as Record<SchemaKind, number>;
    filteredBySearch.forEach((item) => {
      counts[item.kind] += 1;
    });
    return counts;
  }, [filteredBySearch]);

  const toggleFolder = (nodeId: string) => {
    setExpandedFolders((prev) => {
      if (prev.includes(nodeId)) {
        return prev.filter((id) => id !== nodeId);
      }
      return [...prev, nodeId];
    });
  };

  const renderFolderNode = (node: FolderNode) => {
    const isOpen = expandedFolders.includes(node.id);
    const hasChildren = node.children.length > 0;
    const isSelected = selectedFolderId === node.id;
    const domId = toDomId('gql-dev-guide-folder', node.id);
    return (
      <div key={node.id} className="select-none">
        <div 
          className={cn(
            "flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors",
            isSelected ? "bg-blue-100 text-blue-900" : "hover:bg-slate-100 text-slate-700"
          )}
          style={{ paddingLeft: node.depth * 12 + 8 }}
          onClick={() => setSelectedFolderId(node.id)}
          id={domId}
        >
          {hasChildren ? (
            <div 
              onClick={(e) => { e.stopPropagation(); toggleFolder(node.id); }}
              id={`${domId}-toggle`}
              className="p-0.5 hover:bg-slate-200 rounded"
            >
              {isOpen ? <ChevronDown className="h-3 w-3 text-slate-400" /> : <ChevronRight className="h-3 w-3 text-slate-400" />}
            </div>
          ) : (
             <span className="w-4" />
          )}
          
          {hasChildren ? (
            isOpen ? <FolderOpen className="h-4 w-4 text-blue-400" /> : <Folder className="h-4 w-4 text-blue-400" />
          ) : (
            <Folder className="h-4 w-4 text-slate-400" />
          )}
          
          <span className="flex-1 truncate text-sm font-medium">{node.label}</span>
          <span className="text-xs text-slate-400 font-mono">{node.items.length}</span>
        </div>
        
        {hasChildren && isOpen && (
          <div className="mt-0.5">
            {node.children.map((child) => renderFolderNode(child))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex h-[50vh] w-full items-center justify-center bg-slate-50/30">
        <div className="flex flex-col items-center gap-4 text-slate-500">
          <Activity className="h-8 w-8 animate-pulse text-blue-600" />
          <p className="text-sm font-medium">Loading API documentation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-8">
        <div className="flex flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 p-8 text-center">
          <Server className="h-10 w-10 text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-red-900">Unable to load schema</h3>
          <p className="text-sm text-red-700">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-slate-50">
      {/* Top Navigation Bar */}
      <div className="flex h-16 items-center border-b border-slate-200 bg-white px-6 shadow-sm">
         <Link
           to="/admin/dev"
           id="gql-dev-guide-back-link"
           className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors mr-6"
         >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm font-medium">Back</span>
         </Link>
         <div className="flex h-6 w-px bg-slate-200 mr-6" />
         
         <div className="flex items-center gap-2 mr-auto">
            <h1 className="text-lg font-semibold text-slate-900">API Guide</h1>
            <Badge variant="secondary" className="bg-slate-100 text-slate-600 border-slate-200">
                v1.0
            </Badge>
         </div>

         <div className="relative max-w-md w-full">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              id="gql-dev-guide-search-input"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search functions..."
              className="pl-9 bg-slate-50 border-slate-200 focus:bg-white transition-all"
            />
         </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Folders */}
        <div className="w-80 flex-shrink-0 border-r border-slate-200 bg-slate-50 flex flex-col">
            <div className="p-4 border-b border-slate-200">
            <div className="flex items-center justify-between mb-4">
               <h3 className="text-xs font-semibold uppercase text-slate-500 tracking-wider">Explorer</h3>
                  <button
                    id="gql-dev-guide-view-all-button"
                    onClick={() => setSelectedFolderId(null)}
                    className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                  >
                      View All
                  </button>
               </div>
               <div className="flex gap-1 p-1 bg-slate-200 rounded-md">
                   {(['query', 'mutation', 'subscription'] as const).map((kind) => (
                      <button
                        key={kind}
                        id={`gql-dev-guide-filter-${kind}-button`}
                        onClick={() => setSelectedKind(selectedKind === kind ? null : kind)}
                        className={cn(
                            "flex-1 flex items-center justify-center py-1.5 rounded text-xs font-medium transition-all",
                            selectedKind === kind 
                              ? "bg-white text-slate-900 shadow-sm" 
                              : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50"
                        )}
                        title={KIND_LABELS[kind]}
                      >
                         {React.cloneElement(KIND_ICONS[kind] as React.ReactElement, { className: "h-3.5 w-3.5" })}
                         <span className="ml-1.5">{kindCounts[kind]}</span>
                      </button>
                    ))}
               </div>
            </div>
            
            <ScrollArea className="flex-1 px-3 py-4">
                {folderTree.nodes.length === 0 ? (
                    <div className="text-sm text-slate-400 text-center py-8">No folders found</div>
                ) : (
                    <div className="space-y-0.5">
                        {folderTree.nodes.map((node) => renderFolderNode(node))}
                    </div>
                )}
            </ScrollArea>
        </div>

        {/* List Pane (Middle) */}
        {!selectedItem && (
             <div className="flex-1 bg-white flex flex-col overflow-hidden">
               <div className="px-6 py-3 border-b border-slate-200 flex justify-between items-center bg-white sticky top-0 z-10">
                 <div>
                   <h2 className="text-lg font-semibold text-slate-900">
                     {selectedFolderId ? folderTree.nodeMap.get(selectedFolderId)?.label : 'All Functions'}
                   </h2>
                   <p className="text-sm text-slate-500">
                     {selectedFolderItems.length} definitions found
                   </p>
                 </div>
                 <div className="text-xs text-slate-500">
                   {selectedKind ? `Filtered by ${selectedKind}` : 'All kinds'}
                 </div>
               </div>
               
               <ScrollArea className="flex-1">
                 {selectedFolderItems.length === 0 ? (
                   <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                     <Box className="h-12 w-12 mb-4 opacity-20" />
                     <p>No functions match your criteria</p>
                   </div>
                 ) : (
                   <div className="px-6 py-4">
                     <div className="rounded-md border border-slate-200 overflow-hidden">
                       <div className="grid grid-cols-[120px_1fr_160px] bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-600">
                         <span>Kind</span>
                         <span>Name</span>
                         <span className="text-right">Return</span>
                       </div>
                       <div className="divide-y divide-slate-100">
                         {selectedFolderItems.map((item) => (
                           <button
                             key={item.id}
                             id={toDomId('gql-dev-guide-item', item.id)}
                             onClick={() => setSelectedItemId(item.id)}
                             className="grid w-full grid-cols-[120px_1fr_160px] items-center gap-3 px-4 py-2 text-left text-sm hover:bg-slate-50 transition-colors"
                           >
                             <span className="flex items-center gap-2 font-medium text-slate-700">
                               <span
                                 className={cn(
                                   'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold',
                                   item.kind === 'query'
                                     ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                                     : item.kind === 'mutation'
                                     ? 'bg-amber-50 text-amber-700 border border-amber-100'
                                     : 'bg-purple-50 text-purple-700 border border-purple-100'
                                 )}
                               >
                                 {KIND_LABELS[item.kind]}
                               </span>
                               {item.deprecated && (
                                 <Badge variant="destructive" className="h-5 text-xs px-1.5">
                                   Deprecated
                                 </Badge>
                               )}
                             </span>
                             <span className="font-semibold text-slate-900 flex items-center gap-2">
                               {item.name}
                             </span>
                             <span className="text-right font-mono text-xs text-slate-600">
                               {item.returnType || 'void'}
                             </span>
                           </button>
                         ))}
                       </div>
                     </div>
                   </div>
                 )}
               </ScrollArea>
             </div>
        )}

        {/* Detail Pane (Full width when selected) */}
        {selectedItem && (
            <div className="flex-1 bg-white flex flex-col overflow-hidden">
                <div className="border-b border-slate-200 px-6 py-4 flex items-center gap-4 bg-white">
                    <button
                        id="gql-dev-guide-detail-back-button"
                        onClick={() => setSelectedItemId(null)}
                        className="p-2 -ml-2 rounded-full hover:bg-slate-100 text-slate-500 transition-colors"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div className="flex-1">
                        <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                            <span className="flex items-center gap-1">
                                <Folder className="h-3 w-3" />
                                {getPathSegments(selectedItem).join(' / ')}
                            </span>
                        </div>
                        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-3">
                            {selectedItem.name}
                            <Badge variant="outline" className={cn("text-xs font-normal capitalize", 
                                selectedItem.kind === 'query' ? "border-emerald-200 text-emerald-700 bg-emerald-50" : 
                                selectedItem.kind === 'mutation' ? "border-amber-200 text-amber-700 bg-amber-50" : "border-purple-200 text-purple-700 bg-purple-50"
                            )}>
                                {selectedItem.kind}
                            </Badge>
                        </h2>
                    </div>
                </div>

                <ScrollArea className="flex-1">
                    <div className="max-w-4xl mx-auto p-8 space-y-8">
                        {/* Description Section */}
                        <section>
                            <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                                <FileCode className="h-4 w-4 text-slate-400" /> Description
                            </h3>
                            <div className="bg-slate-50 rounded-lg p-6 border border-slate-200 text-slate-700 leading-relaxed">
                                {selectedItem.description || "No description provided for this endpoint."}
                            </div>
                        </section>

                        {/* Parameters Section */}
                        <section>
                            <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                                <Code className="h-4 w-4 text-slate-400" /> Parameters
                            </h3>
                            {formatParameters(selectedItem.parameters).length === 0 ? (
                                <div className="text-sm text-slate-400 italic px-4">No parameters required.</div>
                            ) : (
                                <div className="border border-slate-200 rounded-lg overflow-hidden">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-slate-50 text-xs uppercase text-slate-500 font-semibold">
                                            <tr>
                                                <th className="px-6 py-3 border-b border-slate-200">Name</th>
                                                <th className="px-6 py-3 border-b border-slate-200">Type</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                          {formatParameters(selectedItem.parameters).map((param) => {
                                            const typeName = toTypeName(param.type);
                                            return (
                                              <tr key={param.name} className="hover:bg-slate-50/50">
                                                <td className="px-6 py-4 font-medium text-slate-900">
                                                  {param.name}
                                                </td>
                                                <td className="px-6 py-4 font-mono text-slate-600">
                                                  {typeName ? (
                                                    <Link
                                                      id={toDomId('gql-dev-guide-parameter-type', param.name)}
                                                      to={`/admin/dev/types?type=${encodeURIComponent(typeName)}`}
                                                      className="text-blue-600 hover:underline inline-flex items-center gap-1.5"
                                                    >
                                                      <Box className="h-3.5 w-3.5" />
                                                      {param.type}
                                                    </Link>
                                                  ) : (
                                                    param.type
                                                  )}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>

                        {/* Return Type Section */}
                        <section>
                            <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                                <Braces className="h-4 w-4 text-slate-400" /> Return Type
                            </h3>
                            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                                <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 text-xs font-mono text-slate-500">
                                    Response Object
                                </div>
                                <div className="p-4 font-mono text-sm text-blue-600">
                                    {selectedItem.returnType ? (
                                        <Link
                                            id={toDomId('gql-dev-guide-return-type', selectedItem.returnType)}
                                            to={`/admin/dev/types?type=${encodeURIComponent(
                                                normalizeTypeName(selectedItem.returnType)
                                            )}`}
                                            className="hover:underline flex items-center gap-2"
                                        >
                                            <Box className="h-4 w-4" />
                                            {selectedItem.returnType}
                                        </Link>
                                    ) : (
                                        <span className="text-slate-400">Void</span>
                                    )}
                                </div>
                            </div>
                        </section>
                    </div>
                </ScrollArea>
            </div>
        )}
      </div>
    </div>
  );
};
